import { Modal } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { ICombinedFilters, IFilters } from "@/ts/common-types";
import { titleCase } from "@/ts/utils";
import { FILTER_TYPE_SYMBOLS } from "@/ts/constants";

type TEditRulesFilterModal = {
    editRulesFilterModalData: { show: boolean; data: any };
    setEditRulesFilterModalData: React.Dispatch<
        React.SetStateAction<{ show: boolean; data: any }>
    >;
    rule: Record<string, IFilters>;
};

const EditRulesFiltersModal: React.FC<TEditRulesFilterModal> = ({
    editRulesFilterModalData,
    setEditRulesFilterModalData,
    rule,
}) => {
    const [filtersData, setFiltersData] = useState([]);
    const [inputData, setInputData] =
        useState<Record<string, ICombinedFilters | IFilters>>(null);
    const formRef = useRef(null);

    useEffect(() => {
        const filterNames = [];
        for (const key in rule) {
            if (Object.prototype.hasOwnProperty.call(rule, key)) {
                filterNames.push({
                    name: key,
                    ...rule[key],
                });
            }
        }
        setFiltersData(filterNames);
        setInputData(structuredClone(rule));
    }, [rule]);

    const handleClose = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        save?: boolean
    ) => {
        setEditRulesFilterModalData({
            data: save ? inputData : null,
            show: false,
        });
    };

    const handleChange = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>,
        filterName: string,
        condition?: string
    ) => {
        setInputData((prev) => {
            if (!condition) {
                return {
                    ...prev,
                    [filterName]: {
                        ...(prev[filterName] as IFilters),
                        filter: event.target.value,
                    },
                };
            }

            return {
                ...prev,
                [filterName]: {
                    ...(prev[filterName] as ICombinedFilters),
                    [condition]: {
                        ...(prev[filterName][condition] as IFilters),
                        filter: event.target.value,
                    },
                },
            };
        });
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();
        if (formRef.current.reportValidity()) {
            handleClose(event, true);
        }
    };

    const FooterComponent = () => {
        return (
            <div className="d-flex">
                <span
                    className="alert-panel alert fade show"
                    style={{
                        padding: "0.1em",
                        height: "2em",
                        marginRight: "auto",
                        display: "grid",
                        placeContent: "center",
                    }}
                />
                <button
                    className="btn btn-primary btn-close-big me-2"
                    type="button"
                    style={{ display: "grid", placeContent: "center" }}
                    onClick={handleSubmit}
                >
                    <span
                        className="loader spinner-border spinner-border-sm"
                        style={{ display: "none" }}
                        role="status"
                        aria-hidden="true"
                    />
                    <span className="content">Save Filters</span>
                </button>
            </div>
        );
    };

    return (
        <>
            <Modal
                title={
                    <div className="d-flex align-items-center pb-3">
                        <h4>Edit Filters</h4>
                    </div>
                }
                open={editRulesFilterModalData.show}
                onCancel={handleClose}
                closeIcon={<AiOutlineClose style={{ fontSize: "35px" }} />}
                footer={<FooterComponent />}
                centered
                styles={{
                    body: {
                        maxHeight: "60vh",
                        overflowY: "auto",
                    },
                }}
                width={"35%"}
            >
                <div id="edit-rules-filter-modal" style={{ overflowY: "auto" }}>
                    <div>
                        <form ref={formRef} onSubmit={handleSubmit}>
                            {filtersData.map((filter, index) => {
                                return (
                                    <div
                                        className="input-group frequency-input-group my-3"
                                        key={index}
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-evenly",
                                        }}
                                    >
                                        {!filter.operator && (
                                            <>
                                                <input
                                                    className="frequency form-control text-center"
                                                    defaultValue={`${titleCase(
                                                        filter.name
                                                    )}`}
                                                    disabled={true}
                                                />
                                                <input
                                                    className="frequency form-control text-light text-center operator-input"
                                                    defaultValue={`${
                                                        FILTER_TYPE_SYMBOLS[
                                                            filter.type ||
                                                                filter.filterType
                                                        ]
                                                    }`}
                                                    disabled={true}
                                                />
                                            </>
                                        )}
                                        {(() => {
                                            switch (filter.filterType) {
                                                case "date":
                                                    return (
                                                        <select
                                                            className="frequency form-select text-center"
                                                            required
                                                            defaultValue={
                                                                filter.filter
                                                            }
                                                            onChange={(e) => {
                                                                handleChange(
                                                                    e,
                                                                    filter.name
                                                                );
                                                            }}
                                                        >
                                                            <option disabled>
                                                                Select frequency
                                                            </option>
                                                            <option value="Last 7 Days">
                                                                Last 7 Days
                                                            </option>
                                                            <option value="Last week">
                                                                Last week
                                                            </option>
                                                            <option value="Last 14 Days">
                                                                Last 14 Days
                                                            </option>
                                                            <option value="Last 30 Days">
                                                                Last 30 Days
                                                            </option>
                                                            <option value="Current Month">
                                                                Current Month
                                                            </option>
                                                            <option value="Last Month">
                                                                Last Month
                                                            </option>
                                                        </select>
                                                    );
                                                case "set":
                                                    return (
                                                        <input
                                                            className="frequency form-control text-center"
                                                            type="text"
                                                            defaultValue={filter.values
                                                                .map(
                                                                    (
                                                                        val: string
                                                                    ) => val
                                                                )
                                                                .join(",")}
                                                            disabled
                                                            onChange={(e) => {
                                                                handleChange(
                                                                    e,
                                                                    filter.name
                                                                );
                                                            }}
                                                            step="0.0001"
                                                        />
                                                    );
                                                default:
                                                    if (filter.operator) {
                                                        return (
                                                            <>
                                                                <input
                                                                    className="frequency form-control text-center"
                                                                    defaultValue={`${titleCase(
                                                                        filter.name
                                                                    )}`}
                                                                    disabled={
                                                                        true
                                                                    }
                                                                />
                                                                <input
                                                                    className="frequency form-control text-light text-center operator-input"
                                                                    defaultValue={`${
                                                                        FILTER_TYPE_SYMBOLS[
                                                                            filter
                                                                                .condition1
                                                                                .type ||
                                                                                filter
                                                                                    .condition1
                                                                                    .filterType
                                                                        ]
                                                                    }`}
                                                                    disabled={
                                                                        true
                                                                    }
                                                                />
                                                                <input
                                                                    className="frequency form-control text-center"
                                                                    type={
                                                                        filter
                                                                            .condition1
                                                                            .filterType
                                                                    }
                                                                    defaultValue={
                                                                        filter
                                                                            .condition1
                                                                            .filter
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) => {
                                                                        handleChange(
                                                                            e,
                                                                            filter.name,
                                                                            "condition1"
                                                                        );
                                                                    }}
                                                                    required
                                                                    step="0.0001"
                                                                />
                                                                <input
                                                                    className="frequency form-control text-center text-light text-center operator-input"
                                                                    defaultValue={`${filter.operator.toUpperCase()} ${
                                                                        FILTER_TYPE_SYMBOLS[
                                                                            filter
                                                                                .condition2
                                                                                .type ||
                                                                                filter
                                                                                    .condition2
                                                                                    .filterType
                                                                        ]
                                                                    }`}
                                                                    disabled={
                                                                        true
                                                                    }
                                                                    title={`${filter.operator.toUpperCase()} ${titleCase(
                                                                        filter.name
                                                                    )} ${
                                                                        FILTER_TYPE_SYMBOLS[
                                                                            filter
                                                                                .condition2
                                                                                .type ||
                                                                                filter
                                                                                    .condition2
                                                                                    .filterType
                                                                        ]
                                                                    }`}
                                                                />
                                                                <input
                                                                    className="frequency form-control text-center"
                                                                    type={
                                                                        filter
                                                                            .condition2
                                                                            .filterType
                                                                    }
                                                                    defaultValue={
                                                                        filter
                                                                            .condition2
                                                                            .filter
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) => {
                                                                        handleChange(
                                                                            e,
                                                                            filter.name,
                                                                            "condition2"
                                                                        );
                                                                    }}
                                                                    step="0.0001"
                                                                    required
                                                                />
                                                            </>
                                                        );
                                                    }
                                                    return (
                                                        <input
                                                            className="frequency form-control text-center"
                                                            type={
                                                                filter.filterType
                                                            }
                                                            defaultValue={
                                                                filter.filter
                                                            }
                                                            onChange={(e) => {
                                                                handleChange(
                                                                    e,
                                                                    filter.name
                                                                );
                                                            }}
                                                            step="0.0001"
                                                            required
                                                        />
                                                    );
                                            }
                                        })()}
                                    </div>
                                );
                            })}
                        </form>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default EditRulesFiltersModal;
